/* Free gift on the Add to cart button
   Reads the Globo free-gift widget, works out whether the currently selected
   variant actually qualifies, and if it does, fuses the offer onto the bottom
   of the Add to cart button as a strip. Styling lives in free-gift-cta.css.

   Why the qualification check: the app renders the same card on every variant and
   relies on a footnote ("*Leather Zephyr Only") to carry the restriction. That is
   survivable in a card; fused to the buy button it would be a false promise.
   So the gift's own qualifier is matched against the selected variant's real
   option values, and the strip appears only when they agree.

   The check FAILS CLOSED — see checkQualifies. Every unknown is a "no", and a
   visible swatch that disagrees with the form is enough to withhold the offer.
   The offer promises something free, so showing it on a variant that does not
   get it costs more than not showing it at all, and the app's own card still
   carries the offer and its conditions whenever we decline.

   Copy comes from the app, never from here: the gift name is the widget's title
   and a value ("£24.95") appears only if the campaign copy contains one. Nothing
   is invented client-side, so the strip cannot contradict the app.

   The strip is appended inside the <button> rather than beside it, so the whole
   pill is natively clickable and keyboard operable — no click forwarding, and the
   offer becomes part of the button's accessible name. Safe because product.js
   only ever rewrites the inner .single-add-to-cart-button--text span. */

(function () {
  if (window.freeGiftCtaLoaded) return;
  window.freeGiftCtaLoaded = true;

  /* CAMPAIGN ALLOWLIST — only these Globo campaigns get this treatment; every
     other discount is left exactly as the app renders it.

     Campaigns are named by their own id, never by the number on the widget. That
     number is only the campaign's position in gfg.settings.freeGifts, so deleting
     one in the Globo admin renumbers every campaign after it. When the Jimmy
     Broadbent floor mat campaign was removed, the multi-gift chooser slid from
     position 4 to 3 — straight into this list — and the buy button started
     advertising "Includes Free Simulator Floor Mat" on a campaign whose whole
     point is that the shopper picks which mat they want. Ids do not move. */
  var CAMPAIGN_IDS = [
    "6a8d9ba9007188692032c3ea", // Zephyr Carbon — Free Arm rest pad
  ];

  // Applied to a FULL campaign's widget so the stylesheet can scope to it.
  var MANAGED_CLASS = "fg-gift--managed";
  // Reveals a widget no one is going to restyle — see revealUnmanaged.
  var UNMANAGED_CLASS = "fg-gift--shown";

  var SHOWN_CLASS = "fg-cta--shown";
  // Set while the gift exists but this variant does not qualify: the app's card
  // stays visible and is restyled into a slim value strip by the stylesheet.
  var CONDITION_CLASS = "fg-cta--condition";

  /* Once the gift is in the cart the app rewrites its own title into a state
     message ("Free Arm Rest Pads added!") and drops the qualifier line. Prefixing
     that with our own copy produced "Includes Free Free Arm Rest Pads added!", so
     the two cases are rendered differently and the gift's name is remembered from
     the pre-add title to keep it emphasised either way. */
  var STATE_MESSAGE = /\b(added|applied|claimed|unlocked|in your cart|in cart)\b/i;
  var cachedName = null;

  function norm(s) {
    return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  }

  // App-owned copy goes through innerHTML, so escape it.
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function emphasise(message, name) {
    var haystack = message.toLowerCase();
    var needle = (name || "").toLowerCase();
    if (!needle) return message;
    var at = haystack.indexOf(needle);
    if (at === -1) return message;
    return (
      message.slice(0, at) + "<b>" + message.slice(at, at + needle.length) + "</b>" +
      message.slice(at + needle.length)
    );
  }

  // The gift as the app currently describes it, or null when there is no offer.
  // The allowlisted campaign's widget, or null when this page's gift is not one
  // we manage (in which case nothing below runs and the app keeps its own look).
  function campaignList() {
    return (window.gfg && gfg.settings && gfg.settings.freeGifts) || [];
  }

  // The widget the app rendered for a campaign, found by resolving the campaign's
  // id back to the position the widget id is built from.
  function widgetAt(index) {
    return document.getElementById("FREEGIFTCAMPAIGN-WIDGET-" + index);
  }

  function campaignWidget() {
    var campaigns = campaignList();
    for (var i = 0; i < campaigns.length; i++) {
      if (!campaigns[i] || CAMPAIGN_IDS.indexOf(campaigns[i]._id) === -1) continue;
      var el = widgetAt(i);
      if (el && el.querySelector(".gfgFreeGiftMsgTitle")) return el;
    }
    return null;
  }

  /* Every gift widget starts hidden (see free-gift-cta.css) so none of them can
     paint in the app's default styling before it is known which treatment it
     gets. This reveals the ones nobody is going to restyle.

     Campaigns the shopper picks a gift from are skipped: free-gift-chooser.js
     reveals those as it takes them over, and revealing them here first would put
     back the flash of default styling the hide exists to prevent. */
  function revealUnmanaged() {
    var campaigns = campaignList();
    for (var i = 0; i < campaigns.length; i++) {
      var campaign = campaigns[i];
      if (!campaign) continue;
      if (CAMPAIGN_IDS.indexOf(campaign._id) !== -1) continue;
      if (campaign.isAutoAdd === false) continue;
      var el = widgetAt(i);
      if (el) el.classList.add(UNMANAGED_CLASS);
    }
  }

  function readGift() {
    var widget = campaignWidget();
    if (!widget) return null;
    widget.classList.add(MANAGED_CLASS);

    var titleEl = widget.querySelector(".gfgFreeGiftMsgTitle");
    var title = titleEl ? (titleEl.textContent || "").trim() : "";
    title = title.replace(/[\s*]+$/, "").trim();
    if (!title) return null;

    var subEl = widget.querySelector(".gfgFreeGiftMsgSubTitle");
    var sub = subEl ? (subEl.textContent || "").trim() : "";
    var price = (title + " " + sub).match(/[£$€]\s?\d[\d.,]*/);
    var isStateMessage = STATE_MESSAGE.test(title);

    // Drop a leading "Free" so campaign copy that already says it cannot double up.
    var name = title.replace(/^free\s+/i, "").trim();
    if (isStateMessage) {
      /* On a load that starts in the added state there is no pre-add title to
         learn the name from, so recover it by cutting the state wording off:
         "Arm Rest Pads added!" -> "Arm Rest Pads". */
      name = name.replace(/[\s,;:!.\-]*\b(added|applied|claimed|unlocked|in your cart|in cart)\b.*$/i, "").trim();
    } else if (name) {
      cachedName = name;
    }

    return {
      title: title,
      name: name,
      isStateMessage: isStateMessage,
      qualifier: sub.replace(/^[\s*]+/, "").trim(),
      price: price ? price[0].replace(/\s+/g, "") : null
    };
  }

  /* The selected variant, straight from the form that will be submitted — the
     only authoritative answer to "what is this shopper actually buying". The
     duplicate input the theme renders for the sticky bar is disabled, so skip
     those; fall back to the URL and the theme's own variant select. */
  function selectedVariantId() {
    var inputs = document.querySelectorAll('form input[name="id"]');
    for (var i = 0; i < inputs.length; i++) {
      if (!inputs[i].disabled && inputs[i].value) return inputs[i].value;
    }
    var fromUrl = new URLSearchParams(window.location.search).get("variant");
    if (fromUrl) return fromUrl;
    var select = document.querySelector('select[name="id"]');
    return select && select.value ? select.value : null;
  }

  // Option data rendered by product-information.liquid. Parsed once.
  var variantData;
  function readVariantData() {
    if (variantData !== undefined) return variantData;
    variantData = null;
    var el = document.getElementById("fg-variant-data");
    if (el) {
      try {
        var parsed = JSON.parse(el.textContent);
        if (parsed && parsed.options && parsed.variants) variantData = parsed;
      } catch (e) {
        /* Malformed data means we cannot prove a variant qualifies, and the
           checks below fail closed on a null — which is the right outcome. */
      }
    }
    return variantData;
  }

  /* Every option value on show anywhere in the pickers, whether the theme owns
     the control or an app painted over it. Used to catch a visible selection
     that disagrees with the form: the swatch app can mark "Fabric" selected
     without the variant ever changing, and the gift must not be promised while
     the page reads as a variant that does not get it. */
  function visibleSelections() {
    var out = [];
    document.querySelectorAll(".swatch-button.swatch-selected").forEach(function (button) {
      out.push((button.textContent || "").trim());
    });
    document.querySelectorAll('.variations input[type="radio"]:checked').forEach(function (radio) {
      out.push(radio.value);
    });
    document.querySelectorAll(".variations select[name]").forEach(function (select) {
      out.push(select.value);
    });
    return out.filter(Boolean);
  }

  // Does the qualifier name this option value? Substring rather than equality
  // because campaign copy runs the values into a sentence.
  function mentions(value, qualifierText) {
    var v = norm(value);
    if (!v) return false;
    if (qualifierText.indexOf(v) !== -1) return true;
    // "Carbon-PVC" should still match a qualifier that only says "Carbon".
    var first = v.split(" ")[0];
    return first.length >= 4 && qualifierText.indexOf(first) !== -1;
  }

  /* Which options the qualifier constrains, and to which values. An option is
     constrained when the qualifier names at least one of its values: "Leather
     Zephyr Only" names a Material value, so Material is constrained to Leather
     and every other option is left free. Naming two values of one option reads
     as "either", which is why this is grouped per option rather than flat. */
  function constraints(qualifier) {
    var data = readVariantData();
    if (!data) return null;

    var q = norm(qualifier);
    var found = [];
    data.options.forEach(function (option, index) {
      var required = option.values.filter(function (value) {
        return mentions(value, q);
      });
      if (required.length) found.push({ index: index, required: required });
    });
    return found;
  }

  /* ok: the selected variant qualifies for the gift, proven against real option
     data rather than inferred from the controls.

     Fails closed. Every unknown is a "no": no variant data, an unrecognised
     variant, a qualifier that names no option value we know of, or a visible
     swatch that contradicts what the form has selected. The offer is a promise
     of something free, so showing it wrongly costs more than missing it — and
     the app's own card still carries the offer and its conditions whenever we
     decline to show ours. */
  function checkQualifies(qualifier) {
    var data = readVariantData();
    if (!data) return { ok: false };

    var id = selectedVariantId();
    var selected = id ? data.variants[id] : null;
    if (!selected) return { ok: false };

    var required = constraints(qualifier);
    if (!required || !required.length) {
      /* The qualifier names no option value of this product, so there is no
         variant-level restriction we can check. What that means depends on how
         many variants there are.

         One variant: the offer is provable. Product-level scoping is the app's
         own job and it does that part correctly — it only renders the widget on
         products the campaign covers. The variant check exists solely because the
         app cannot distinguish variants *within* one product, and with a single
         variant there is nothing to distinguish. This is the "Free Floor Mat with
         every Jimmy Broadbent Cockpit" shape: one variant, no restriction to
         verify, every purchase gets the gift.

         More than one variant: we cannot tell which of them qualify, so withhold
         — this is the case that had the badge promising arm rest pads on Fabric
         Zephyrs. */
      return { ok: Object.keys(data.variants).length === 1 };
    }

    var ok = true;
    var mentionedValues = [];
    required.forEach(function (constraint) {
      constraint.required.forEach(function (value) {
        mentionedValues.push(norm(value));
      });
      if (constraint.required.indexOf(selected[constraint.index]) === -1) ok = false;
    });
    if (!ok) return { ok: false };

    /* The form says this variant qualifies. Now check nothing on screen says
       otherwise: for each constrained option, some visible selection must still
       match one of the values the qualifier allows. If the shopper has clicked
       "Fabric" and only the app's UI has caught up, this is what holds the
       badge back until the page agrees with itself. */
    var visible = visibleSelections().map(norm);
    var contradicted = required.some(function (constraint) {
      var allowed = constraint.required.map(norm);
      var optionValues = data.options[constraint.index].values.map(norm);
      var seen = visible.filter(function (value) {
        return optionValues.indexOf(value) !== -1;
      });
      // Nothing visible for this option — nothing to contradict.
      if (!seen.length) return false;
      /* Every visible value for the option has to be an allowed one, not just
         one of them. Two widgets own this option (the theme's select and the
         app's buttons) and they can disagree — the app marking "Fabric" while
         the select still reads "Leather" is exactly the case that put the badge
         on a Fabric-looking page. Any disallowed value on screen withholds it. */
      return seen.some(function (value) {
        return allowed.indexOf(value) === -1;
      });
    });
    if (contradicted) return { ok: false };

    return { ok: true };
  }

  function plusMark() {
    return '<span class="fg-cta__plus" aria-hidden="true">+</span>';
  }

  function clearStrip() {
    var strip = document.querySelector(".fg-cta__strip");
    if (strip) strip.remove();
    var button = document.getElementById("AddToCart");
    if (button) button.classList.remove("fg-cta-btn");
    document.documentElement.classList.remove(SHOWN_CLASS);
  }

  function apply() {
    revealUnmanaged();

    var button = document.getElementById("AddToCart");
    if (!button) return;

    var root = document.documentElement;
    var gift = readGift();
    if (!gift) {
      clearStrip();
      root.classList.remove(CONDITION_CLASS);
      return;
    }

    /* Three states: qualifying (strip on the button), not qualifying (app card
       restyled as a value strip so the offer stays visible with the condition
       that unlocks it), and no gift at all. */
    var verdict = checkQualifies(gift.qualifier);
    if (!verdict.ok) {
      clearStrip();
      root.classList.add(CONDITION_CLASS);
      return;
    }
    root.classList.remove(CONDITION_CLASS);

    // Nothing to promise on the button while the variant is sold out.
    if (button.disabled) {
      clearStrip();
      return;
    }

    var text;
    if (gift.isStateMessage) {
      // The app's message is already a full sentence — show it as written.
      text = emphasise(esc(gift.title), cachedName || gift.name);
    } else {
      text = "Includes Free <b>" + esc(gift.name) + "</b>";
      if (gift.price) text += " — " + esc(gift.price) + " value";
    }

    // Render inside the button itself so both halves of the pill are the button.
    button.classList.add("fg-cta-btn");

    /* Move the label and the theme's spinner into a wrapper that carries the red
       fill. The theme zeroes the opacity of every span in a loading button, so a
       fill on the label itself vanished on press and left a blank outline. */
    var label = button.querySelector(".single-add-to-cart-button--text");
    if (label && !button.querySelector(".fg-cta__main")) {
      var main = document.createElement("span");
      main.className = "fg-cta__main";
      label.parentNode.insertBefore(main, label);
      main.appendChild(label);
      /* The running-total pill and its arrow (atc-running-total.liquid) are
         siblings of the label, so they have to come along or they'd be left
         behind as direct children of the button — which is display:block here,
         putting the price on its own line between the two halves. */
      var runningTotal = button.querySelector("[data-atc-total]");
      if (runningTotal) main.appendChild(runningTotal);
      var totalArrow = button.querySelector(".atc-total-arrow");
      if (totalArrow) main.appendChild(totalArrow);
      var overlay = button.querySelector(".loading-overlay");
      if (overlay) main.appendChild(overlay);
    }

    var strip = button.querySelector(".fg-cta__strip");
    if (!strip) {
      strip = document.createElement("span");
      strip.className = "fg-cta__strip";
      button.appendChild(strip);
    }

    var html = plusMark() + "<span>" + text + "</span>";
    if (strip.innerHTML !== html) strip.innerHTML = html;
    document.documentElement.classList.add(SHOWN_CLASS);
  }

  function start() {
    apply();
    // The widget and the button both arrive asynchronously.
    var interval = setInterval(apply, 400);
    setTimeout(function () {
      clearInterval(interval);
    }, 15000);

    /* Variant changes re-render the section, so re-check on every DOM change.
       This has to observe document.body, not .product-information--inner: the
       re-render replaces that container, which would leave the observer bound to
       a detached node and silently stop firing after the first variant switch.
       apply() is idempotent, so re-entry from our own mutation settles at once.

       class is watched as well as childList because the swatch app signals which
       option is selected by toggling .swatch-selected — an attribute-only change.
       With childList alone, a swatch click more than 15s after load (once the
       startup poll below has stopped) never re-ran the check, so the strip
       dropped on the way to Fabric and never came back on the way to Leather.
       Filtered to class specifically: Flickity rewrites inline transforms on
       every frame of a drag, and watching all attributes would wake us for each
       one. The rAF gate collapses whatever bursts do arrive into one pass. */
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        apply();
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"]
    });

    /* A change event fires before the re-render it triggers has landed, so the
       state read at that instant is the old one — re-check as the new markup
       arrives rather than only on the click itself. */
    document.addEventListener(
      "change",
      function () {
        apply();
        [400, 1200, 2500].forEach(function (delay) {
          setTimeout(apply, delay);
        });
      },
      true
    );
  }

  /* Optional hook for the Globo app's "Custom JS scripts" panel. The observer
     already catches the app rewriting its own markup, so this is not required —
     it just lets the app tell us directly instead of us noticing, e.g. one line
     in "after free gift addition":
       window.freeGiftCta && window.freeGiftCta.refresh(); */
  window.freeGiftCta = { refresh: apply };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
